import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { FeatureLevel, RoleFeature } from "@/lib/api";
import {
  ADMIN_ROLE,
  customGroups,
  featureCopy,
  groupLabel,
  groupNameProblem,
  isBelow,
  LEVEL_HINT,
  LEVEL_LABEL,
  STANDARD_ROLE,
} from "@/lib/people-levels";
import { cn } from "@/lib/utils";
import { useId, useState } from "react";

const SELECTED_DOT: Record<FeatureLevel, string> = {
  auto: "bg-emerald-500",
  confirm: "bg-amber-500",
  disabled: "bg-muted-foreground/40",
};

const LEVELS: FeatureLevel[] = ["auto", "confirm", "disabled"];

const NEXT_KEY: Record<string, 1 | -1> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

const LOCKED_HINT = "Admins already get this through Standard. Change it on Standard.";

export function FeatureRow({
  feature,
  value,
  editable,
  busy,
  floor,
  groupName,
  onChange,
}: {
  feature: RoleFeature;
  value: FeatureLevel;
  editable: boolean;
  busy: boolean;
  // Admins only: what Standard already gives them, when that's more than
  // their own setting. Shown as the selected level; lower ones are locked.
  floor: FeatureLevel | null;
  groupName: string;
  onChange: (level: FeatureLevel) => void;
}) {
  const copy = featureCopy(feature);
  const throughId = useId();
  const shown = floor && isBelow(value, floor) ? floor : value;
  const locked = (l: FeatureLevel) => floor !== null && isBelow(l, floor);
  const through = floor && shown === floor && value !== floor ? `${LEVEL_LABEL[floor]} for everyone through Standard` : null;
  const choices = LEVELS.filter((l) => l !== "confirm" || feature.approvable || shown === "confirm");
  const open = choices.filter((l) => !locked(l));
  const tabStop = open.includes(shown) ? shown : open[0];
  const pick = (l: FeatureLevel) => editable && !busy && !locked(l) && shown !== l && onChange(l);
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const step = NEXT_KEY[e.key];
    if (!step) return;
    e.preventDefault();
    if (busy || open.length === 0) return;
    const at = open.indexOf((document.activeElement as HTMLElement | null)?.dataset.level as FeatureLevel);
    const next = open[(Math.max(at, 0) + step + open.length) % open.length];
    e.currentTarget.querySelector<HTMLButtonElement>(`[data-level="${next}"]`)?.focus();
    pick(next);
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 px-4 py-3">
      <div className="min-w-0 flex-1 basis-64">
        <p className="text-sm font-medium">{copy.title}</p>
        <p className="text-xs text-muted-foreground text-pretty">{copy.summary}</p>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {through && (
          <span id={throughId} className="text-xs text-muted-foreground">
            {through}
          </span>
        )}
        <div
          className="flex shrink-0 rounded-md border border-border bg-muted/50 p-0.5"
          role="radiogroup"
          aria-label={`${copy.title} for ${groupName}`}
          aria-describedby={through ? throughId : undefined}
          aria-readonly={!editable || undefined}
          aria-busy={busy || undefined}
          onKeyDown={onKeyDown}
        >
          {choices.map((l) => (
            <Tooltip key={l}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  role="radio"
                  data-level={l}
                  tabIndex={l === tabStop ? 0 : -1}
                  aria-checked={shown === l}
                  aria-disabled={!editable || locked(l) || undefined}
                  aria-description={locked(l) ? LOCKED_HINT : LEVEL_HINT[l]}
                  onClick={() => pick(l)}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    shown === l
                      ? "bg-background font-medium text-foreground shadow-sm ring-1 ring-border"
                      : "text-muted-foreground",
                    editable && shown !== l && !locked(l) && "hover:text-foreground",
                    !editable && "cursor-default",
                    locked(l) && "cursor-not-allowed",
                    (!editable || locked(l)) && shown !== l && "opacity-50",
                  )}
                >
                  {shown === l && <span className={cn("size-1.5 shrink-0 rounded-full", SELECTED_DOT[l])} />}
                  {LEVEL_LABEL[l]}
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" collisionPadding={12} className="max-w-64 text-pretty">
                {locked(l) ? LOCKED_HINT : LEVEL_HINT[l]}
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </div>
    </div>
  );
}

export function NewGroupDialog({
  existing,
  busy,
  onClose,
  onAdd,
}: {
  existing: string[];
  busy: boolean;
  onClose: () => void;
  onAdd: (name: string, from: string) => void;
}) {
  const [name, setName] = useState("");
  const [from, setFrom] = useState(STANDARD_ROLE);
  const [touched, setTouched] = useState(false);
  const [namesAtOpen] = useState(existing);
  const problem = groupNameProblem(name, namesAtOpen);
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="grid-cols-[minmax(0,1fr)] sm:max-w-md">
        <form
          className="flex min-w-0 flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            setTouched(true);
            if (!problem && !busy) onAdd(name.trim(), from);
          }}
        >
          <DialogHeader>
            <DialogTitle>New group</DialogTitle>
            <DialogDescription className="text-pretty">
              A group is a set of people who can do the same things in chat. Groups don&apos;t give anyone access to this
              dashboard.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="group-name">Name</Label>
            <Input
              id="group-name"
              autoFocus
              placeholder="e.g. Finance team"
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-invalid={touched && problem ? true : undefined}
            />
            {touched && problem && <p className="text-xs text-destructive">{problem}</p>}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="group-from">Copy settings from</Label>
            <Select value={from} onValueChange={setFrom}>
              <SelectTrigger id="group-from" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[STANDARD_ROLE, ADMIN_ROLE, ...customGroups(Object.fromEntries(namesAtOpen.map((e) => [e, 1])))].map((r) => (
                  <SelectItem key={r} value={r}>
                    {groupLabel(r)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" loading={busy}>
              Create group
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
